export const ROUTE_PATHS = {
  HOME: '/',
  DIAGNOSIS: '/diagnosis',
  PRODUCTS: '/products',
  MANAGEMENT: '/management',
  ABOUT: '/about',
} as const;

export type ConstitutionType = '태양인' | '태음인' | '소양인' | '소음인';

export type ElementType = '地' | '水' | '火' | '風';

export interface ConstitutionInfo {
  id: ConstitutionType;
  name: string;
  element: ElementType;
  description: string;
  traits: string[];
  organBalance: {
    strong: string;
    weak: string;
  };
  direction: string;
}

export interface DiagnosisResult {
  constitution: ConstitutionType;
  scores: Record<ConstitutionType, number>;
  recommendedProductType: ElementType;
  analysisDate: string;
  userFeedback?: {
    digestion: number;
    energy: number;
    sustainability: number;
  };
}

export interface ProteinProduct {
  id: string;
  name: string;
  element: ElementType;
  targetConstitution: ConstitutionType;
  tagline: string;
  description: string;
  keyBenefits: string[];
  mainIngredients: string[];
  price: number;
  imageUrl: string;
}

export const CONSTITUTION_TYPES: Record<ConstitutionType, ConstitutionInfo> = {
  태양인: {
    id: '태양인',
    name: '태양인 (Tae-Yang)',
    element: '風',
    description: '폐가 크고 간이 작은 체질로, 기운이 위로 솟구치고 발산하는 성질이 강합니다.',
    traits: ['창의적이고 과감함', '상체 발달형', '소화력이 다소 약할 수 있음'],
    organBalance: {
      strong: '폐(肺)',
      weak: '간(肝)',
    },
    direction: '발산 (Expansion)',
  },
  태음인: {
    id: '태음인',
    name: '태음인 (Tae-Um)',
    element: '地',
    description: '간이 크고 폐가 작은 체질로, 기운을 안으로 모으고 저장하는 성질이 강합니다.',
    traits: ['체구가 건실함', '지구력이 좋음', '체중 증가가 쉬운 편'],
    organBalance: {
      strong: '간(肝)',
      weak: '폐(肺)',
    },
    direction: '저장 (Storage)',
  },
  소양인: {
    id: '소양인',
    name: '소양인 (So-Yang)',
    element: '火',
    description: '비위가 크고 신장이 작은 체질로, 기운이 밖으로 퍼지며 열이 많은 성질이 있습니다.',
    traits: ['민첩하고 활동적', '흉곽 발달형', '열이 많아 갈증을 자주 느낌'],
    organBalance: {
      strong: '비(脾)',
      weak: '신(腎)',
    },
    direction: '소모 (Consumption)',
  },
  소음인: {
    id: '소음인',
    name: '소음인 (So-Um)',
    element: '水',
    description: '신장이 크고 비위가 작은 체질로, 기운이 아래로 가라앉고 수렴하는 성질이 강합니다.',
    traits: ['꼼꼼하고 침착함', '하체 발달형', '소화기가 차고 약한 편'],
    organBalance: {
      strong: '신(腎)',
      weak: '비(脾)',
    },
    direction: '수렴 (Convergence)',
  },
};

export function getElementByConstitution(constitution: ConstitutionType): ElementType {
  return CONSTITUTION_TYPES[constitution].element;
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
  }).format(price);
}

export function getConstitutionFromScores(scores: Record<ConstitutionType, number>): ConstitutionType {
  return Object.entries(scores).reduce((a, b) => (a[1] > b[1] ? a : b))[0] as ConstitutionType;
}
